
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const Confetti = () => {
  const [particles, setParticles] = useState([]);
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const createParticle = () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      size: Math.random() * 8 + 2,
      color: `hsl(${Math.random() * 360}, 100%, 50%)`,
      speedY: (Math.random() - 0.5) * 30,
      speedX: (Math.random() - 0.5) * 30,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 10,
    });

    const initialParticles = Array.from({ length: 300 }, createParticle);
    setParticles(initialParticles);

    const animateParticles = () => {
      setParticles(prevParticles =>
        prevParticles.map(particle => ({
          ...particle,
          y: particle.y + particle.speedY,
          x: particle.x + particle.speedX,
          speedY: particle.speedY * 0.99,
          speedX: particle.speedX * 0.99,
          rotation: (particle.rotation + particle.rotationSpeed) % 360,
        })).filter(particle => 
          particle.y > -50 && 
          particle.y < window.innerHeight + 50 && 
          particle.x > -50 && 
          particle.x < window.innerWidth + 50
        )
      );
    };

    const intervalId = setInterval(animateParticles, 16);

    const timeoutId = setTimeout(() => {
      setShowConfetti(false);
    }, 2000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, []);

  if (!showConfetti) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9999 }}>
      {particles.map((particle, index) => (
        <div
          key={index}
          style={{
            position: 'absolute',
            left: `${particle.x}px`,
            top: `${particle.y}px`,
            width: 0,
            height: 0,
            borderLeft: `${particle.size / 2}px solid transparent`,
            borderRight: `${particle.size / 2}px solid transparent`,
            borderBottom: `${particle.size}px solid ${particle.color}`,
            transform: `rotate(${particle.rotation}deg)`,
          }}
        />
      ))}
    </div>
  );
};

const ProblemModal = ({ isOpen, onClose, user }) => {
  const [problemDescription, setProblemDescription] = useState('');
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  const validateProblemDescription = (description) => {
    if (description.trim().length < 10) {
      setError('La descripción debe tener al menos 10 caracteres');
      return false;
    }
    if (description.trim().length > 500) {
      setError('La descripción no puede exceder 500 caracteres');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!validateProblemDescription(problemDescription)) {
      return;
    }
    try {
      await axios.post('https://api.mediscribe.io/api/reported-issues?populate=*', {
        data: {
          description: problemDescription,
          date: new Date().toISOString(),
          user_id: user.id
        }
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setShowThankYouModal(true);
    } catch (error) {
      console.error('Error al enviar el problema:', error);
    }
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowThankYouModal(false);
    setProblemDescription('');
    setError('');
    onClose();
  };

  const handleTextareaChange = (e) => {
    e.stopPropagation();
    setProblemDescription(e.target.value);
    validateProblemDescription(e.target.value);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="text-2xl title-green">Cuéntanos el problema</DialogTitle>
          </DialogHeader>

          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
          <Textarea
            placeholder="Describe el problema que encontraste..."
            value={problemDescription}
            onChange={handleTextareaChange}
            onClick={(e) => e.stopPropagation()}
            rows={5}
          />
          
          <DialogFooter className="pb-6">
            <Button 
              type="button"
              variant="outline" 
              onClick={handleClose} 
              className="hidden md:flex bg-black text-white hover:bg-gray-800 hover:text-white"
            >
              Cancelar
            </Button>
            <Button 
              type="button"
              onClick={handleSubmit} 
              className="mr-auto bg-orange-500 hover:bg-orange-600 text-white" 
              disabled={!!error}
            >
              Enviar reporte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showThankYouModal} onOpenChange={handleClose}>
        <DialogContent className="min-h-[300px] flex flex-col justify-center items-center text-center">
          <DialogHeader className="flex flex-col items-center">
            <DialogTitle className="text-2xl title-green">¡Gracias por tu retroalimentación!</DialogTitle>
            <p className="mt-2 text-gray-600">Hemos recibido tu reporte del problema</p>
            <Confetti />
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProblemModal;
